import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from "react";
import { makeStyles } from "@material-ui/core/styles";
import { useHistory } from "react-router-dom";
import Drawer from "@material-ui/core/Drawer";
import {
  BarChart,
  Bar,
  CartesianGrid,
  Cell,
  PieChart,
  Pie,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from "recharts";
import { AuthContext } from "../../context/Auth/AuthContext";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";
import {
  KoraxPage,
  KoraxPageHeader
} from "../../components/KoraxUI";
import ControlCenterNav from "./components/ControlCenterNav";
import ReleasesModule from "./components/ReleasesModule";

const useStyles = makeStyles(theme => ({
  root: {
    flex: 1,
    width: "100%",
    height: "100%",
    overflow: "auto",
    background:
      theme.palette.type === "dark"
        ? "#080E1C"
        : "#F8FAFC",
    fontFamily: "'Inter', system-ui, sans-serif",
    ...theme.scrollbarStyles
  },

  inner: {
    width: "100%",
    maxWidth: 1440,
    margin: "0 auto",
    display: "flex",
    flexDirection: "column",
    gap: 18
  },

  commandHero: {
    position: "relative",
    overflow: "hidden",
    padding: "28px 30px",
    borderRadius: 24,
    color: "#FFFFFF",
    background:
      "linear-gradient(135deg,#071329 0%,#0B2C68 52%,#155EEF 100%)",
    boxShadow:
      "0 22px 48px rgba(15,43,94,.24)",
    "&::before": {
      content: '""',
      position: "absolute",
      width: 340,
      height: 340,
      right: -110,
      top: -180,
      borderRadius: "50%",
      background: "rgba(255,255,255,.09)"
    },
    "&::after": {
      content: '""',
      position: "absolute",
      width: 220,
      height: 220,
      right: 120,
      bottom: -170,
      borderRadius: "50%",
      background: "rgba(96,165,250,.18)"
    },
    [theme.breakpoints.down("sm")]: {
      padding: "22px 20px",
      borderRadius: 19
    }
  },

  heroTop: {
    position: "relative",
    zIndex: 1,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 20,
    flexWrap: "wrap"
  },

  heroEyebrow: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    marginBottom: 10,
    color: "#BFDBFE",
    fontSize: "0.67rem",
    fontWeight: 850,
    textTransform: "uppercase",
    letterSpacing: ".12em"
  },

  liveDot: {
    width: 8,
    height: 8,
    borderRadius: "50%",
    background: "#4ADE80",
    boxShadow: "0 0 0 5px rgba(74,222,128,.13)"
  },

  heroTitle: {
    margin: 0,
    fontSize: "clamp(1.55rem,3vw,2.35rem)",
    fontWeight: 900,
    letterSpacing: "-.045em",
    lineHeight: 1.06
  },

  heroSubtitle: {
    maxWidth: 650,
    marginTop: 10,
    color: "rgba(219,234,254,.82)",
    fontSize: "0.79rem",
    lineHeight: 1.65
  },

  heroActions: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    flexWrap: "wrap"
  },

  heroRefresh: {
    minHeight: 40,
    padding: "9px 15px",
    borderRadius: 11,
    border: "1px solid rgba(255,255,255,.22)",
    background: "rgba(255,255,255,.12)",
    color: "#FFFFFF",
    cursor: "pointer",
    fontFamily: "inherit",
    fontSize: "0.73rem",
    fontWeight: 850,
    backdropFilter: "blur(8px)",
    "&:hover": {
      background: "rgba(255,255,255,.18)"
    },
    "&:disabled": {
      opacity: 0.55,
      cursor: "not-allowed"
    }
  },

  heroStats: {
    position: "relative",
    zIndex: 1,
    display: "grid",
    gridTemplateColumns:
      "repeat(4,minmax(0,1fr))",
    gap: 12,
    marginTop: 24,
    [theme.breakpoints.down("md")]: {
      gridTemplateColumns:
        "repeat(2,minmax(0,1fr))"
    },
    [theme.breakpoints.down("xs")]: {
      gridTemplateColumns: "1fr"
    }
  },

  heroStat: {
    minWidth: 0,
    padding: "15px 16px",
    borderRadius: 15,
    background: "rgba(255,255,255,.10)",
    border: "1px solid rgba(255,255,255,.14)",
    backdropFilter: "blur(8px)"
  },

  heroStatLabel: {
    color: "rgba(219,234,254,.72)",
    fontSize: "0.64rem",
    fontWeight: 800,
    textTransform: "uppercase",
    letterSpacing: ".055em"
  },

  heroStatValue: {
    marginTop: 7,
    color: "#FFFFFF",
    fontSize: "1.22rem",
    fontWeight: 900,
    letterSpacing: "-.035em",
    overflow: "hidden",
    textOverflow: "ellipsis"
  },

  operationBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 14,
    padding: "13px 16px",
    borderRadius: 15,
    background:
      theme.palette.type === "dark"
        ? "#0D1626"
        : "#FFFFFF",
    border: `1px solid ${
      theme.palette.type === "dark"
        ? "rgba(255,255,255,.07)"
        : "#E8EDF5"
    }`,
    flexWrap: "wrap"
  },

  operationItems: {
    display: "flex",
    alignItems: "center",
    gap: 9,
    flexWrap: "wrap"
  },

  operationChip: {
    display: "inline-flex",
    alignItems: "center",
    gap: 7,
    minHeight: 31,
    padding: "6px 10px",
    borderRadius: 999,
    color:
      theme.palette.type === "dark"
        ? "#CBD5E1"
        : "#334155",
    background:
      theme.palette.type === "dark"
        ? "rgba(255,255,255,.04)"
        : "#F8FAFC",
    border: `1px solid ${
      theme.palette.type === "dark"
        ? "rgba(255,255,255,.06)"
        : "#E8EDF5"
    }`,
    fontSize: "0.67rem",
    fontWeight: 800
  },

  chipDot: {
    width: 7,
    height: 7,
    borderRadius: "50%"
  },

  sectionHeading: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-end",
    gap: 14,
    marginTop: 2,
    flexWrap: "wrap"
  },

  sectionHeadingTitle: {
    color:
      theme.palette.type === "dark"
        ? "#F8FAFC"
        : "#0F172A",
    fontSize: "1rem",
    fontWeight: 900,
    letterSpacing: "-.02em"
  },

  sectionHeadingText: {
    marginTop: 4,
    color: "#94A3B8",
    fontSize: "0.7rem"
  },

  chartsGrid: {
    display: "grid",
    gridTemplateColumns:
      "minmax(0, 1.35fr) repeat(2, minmax(280px, .82fr))",
    gap: 16,
    [theme.breakpoints.down("md")]: {
      gridTemplateColumns: "1fr"
    }
  },

  chartCard: {
    minWidth: 0,
    minHeight: 350,
    padding: "20px",
    borderRadius: 19,
    background:
      theme.palette.type === "dark"
        ? "#0D1626"
        : "#FFFFFF",
    border: `1px solid ${
      theme.palette.type === "dark"
        ? "rgba(255,255,255,.07)"
        : "#E8EDF5"
    }`,
    boxShadow:
      "0 5px 22px rgba(15,23,42,.05)"
  },

  chartHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 12,
    marginBottom: 16
  },

  chartTitle: {
    color:
      theme.palette.type === "dark"
        ? "#F8FAFC"
        : "#0F172A",
    fontSize: "0.88rem",
    fontWeight: 900,
    letterSpacing: "-.015em"
  },

  chartSubtitle: {
    marginTop: 4,
    color: "#94A3B8",
    fontSize: "0.68rem",
    lineHeight: 1.45
  },

  chartBody: {
    width: "100%",
    height: 245
  },

  chartLegend: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 14,
    flexWrap: "wrap",
    marginTop: 8
  },

  chartLegendItem: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    color:
      theme.palette.type === "dark"
        ? "#CBD5E1"
        : "#475569",
    fontSize: "0.66rem",
    fontWeight: 750
  },

  chartLegendDot: {
    width: 8,
    height: 8,
    borderRadius: "50%"
  },

  kpiGrid: {
    display: "grid",
    gridTemplateColumns:
      "repeat(auto-fit, minmax(190px, 1fr))",
    gap: 14
  },

  kpiCard: {
    minHeight: 132,
    padding: "18px 18px 16px",
    borderRadius: 17,
    background:
      theme.palette.type === "dark"
        ? "#0D1626"
        : "#FFFFFF",
    border: `1px solid ${
      theme.palette.type === "dark"
        ? "rgba(255,255,255,.07)"
        : "#E8EDF5"
    }`,
    boxShadow: "0 4px 18px rgba(15,23,42,.045)"
  },

  kpiTop: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10
  },

  kpiIcon: {
    width: 38,
    height: 38,
    borderRadius: 12,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 17,
    fontWeight: 900
  },

  kpiLabel: {
    color: "#64748B",
    fontSize: "0.72rem",
    fontWeight: 750,
    letterSpacing: ".02em"
  },

  kpiValue: {
    marginTop: 14,
    color:
      theme.palette.type === "dark"
        ? "#F8FAFC"
        : "#0F172A",
    fontSize: "1.55rem",
    fontWeight: 900,
    letterSpacing: "-.035em",
    lineHeight: 1
  },

  kpiHint: {
    marginTop: 8,
    color: "#94A3B8",
    fontSize: "0.68rem",
    lineHeight: 1.4
  },

  sectionGrid: {
    display: "grid",
    gridTemplateColumns:
      "minmax(0, 1.5fr) minmax(300px, .8fr)",
    gap: 16,
    [theme.breakpoints.down("md")]: {
      gridTemplateColumns: "1fr"
    }
  },

  card: {
    minWidth: 0,
    padding: "20px",
    borderRadius: 18,
    background:
      theme.palette.type === "dark"
        ? "#0D1626"
        : "#FFFFFF",
    border: `1px solid ${
      theme.palette.type === "dark"
        ? "rgba(255,255,255,.07)"
        : "#E8EDF5"
    }`,
    boxShadow: "0 4px 18px rgba(15,23,42,.045)"
  },

  cardHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 12,
    marginBottom: 16,
    flexWrap: "wrap"
  },

  cardTitle: {
    color:
      theme.palette.type === "dark"
        ? "#F8FAFC"
        : "#0F172A",
    fontSize: "0.92rem",
    fontWeight: 850
  },

  cardSubtitle: {
    marginTop: 4,
    color: "#94A3B8",
    fontSize: "0.72rem",
    lineHeight: 1.45
  },

  refreshButton: {
    minHeight: 36,
    padding: "8px 14px",
    borderRadius: 10,
    border: "1px solid rgba(37,99,235,.22)",
    background: "rgba(37,99,235,.07)",
    color: "#1D4ED8",
    cursor: "pointer",
    fontFamily: "inherit",
    fontSize: "0.74rem",
    fontWeight: 800,
    "&:disabled": {
      opacity: 0.45,
      cursor: "not-allowed"
    }
  },

  tableWrap: {
    width: "100%",
    overflowX: "auto"
  },

  table: {
    width: "100%",
    minWidth: 930,
    borderCollapse: "collapse"
  },

  th: {
    padding: "10px 12px",
    textAlign: "left",
    color: "#94A3B8",
    fontSize: "0.66rem",
    fontWeight: 850,
    textTransform: "uppercase",
    letterSpacing: ".06em",
    borderBottom: `1px solid ${
      theme.palette.type === "dark"
        ? "rgba(255,255,255,.06)"
        : "#EEF2F7"
    }`,
    whiteSpace: "nowrap"
  },

  td: {
    padding: "13px 12px",
    color:
      theme.palette.type === "dark"
        ? "#CBD5E1"
        : "#334155",
    fontSize: "0.75rem",
    borderBottom: `1px solid ${
      theme.palette.type === "dark"
        ? "rgba(255,255,255,.045)"
        : "#F1F5F9"
    }`,
    verticalAlign: "middle"
  },

  statusBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
    padding: "4px 8px",
    borderRadius: 999,
    fontSize: "0.65rem",
    fontWeight: 800,
    whiteSpace: "nowrap"
  },

  timeline: {
    display: "flex",
    flexDirection: "column"
  },

  timelineItem: {
    position: "relative",
    display: "grid",
    gridTemplateColumns: "34px minmax(0,1fr)",
    gap: 10,
    paddingBottom: 16,
    "&:last-child": {
      paddingBottom: 0
    },
    "&:not(:last-child)::after": {
      content: '""',
      position: "absolute",
      left: 16,
      top: 34,
      bottom: 2,
      width: 1,
      background:
        theme.palette.type === "dark"
          ? "rgba(255,255,255,.08)"
          : "#E2E8F0"
    }
  },

  timelineDot: {
    width: 32,
    height: 32,
    borderRadius: 11,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "rgba(37,99,235,.10)",
    color: "#2563EB",
    fontSize: 13,
    fontWeight: 900,
    zIndex: 1
  },

  eventTitle: {
    color:
      theme.palette.type === "dark"
        ? "#F1F5F9"
        : "#0F172A",
    fontSize: "0.76rem",
    fontWeight: 800,
    lineHeight: 1.35
  },

  eventMeta: {
    marginTop: 3,
    color: "#94A3B8",
    fontSize: "0.66rem",
    lineHeight: 1.45
  },

  creditGrid: {
    display: "grid",
    gridTemplateColumns:
      "repeat(auto-fit, minmax(150px, 1fr))",
    gap: 12
  },

  creditCard: {
    padding: "15px",
    borderRadius: 13,
    background:
      theme.palette.type === "dark"
        ? "rgba(255,255,255,.03)"
        : "#F8FAFC",
    border: `1px solid ${
      theme.palette.type === "dark"
        ? "rgba(255,255,255,.055)"
        : "#E8EDF5"
    }`
  },

  creditValue: {
    fontSize: "1.2rem",
    fontWeight: 900,
    letterSpacing: "-.025em"
  },

  creditLabel: {
    marginTop: 4,
    color: "#94A3B8",
    fontSize: "0.67rem",
    lineHeight: 1.4
  },

  empty: {
    padding: "32px 12px",
    textAlign: "center",
    color: "#94A3B8",
    fontSize: "0.76rem",
    lineHeight: 1.6
  },

  denied: {
    maxWidth: 520,
    margin: "60px auto",
    padding: "28px",
    textAlign: "center",
    borderRadius: 18,
    background:
      theme.palette.type === "dark"
        ? "#0D1626"
        : "#FFFFFF",
    border: "1px solid #E8EDF5"
  },

  drawerPaper: {
    width: 520,
    maxWidth: "100vw",
    padding: 0,
    background:
      theme.palette.type === "dark"
        ? "#0D1626"
        : "#FFFFFF"
  },

  drawerHeader: {
    padding: "18px 20px",
    borderBottom: `1px solid ${
      theme.palette.type === "dark"
        ? "rgba(255,255,255,.07)"
        : "#E8EDF5"
    }`,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12
  },

  drawerBody: {
    padding: 20,
    overflowY: "auto"
  },

  detailGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 12,
    marginBottom: 18
  },

  detailItem: {
    padding: 12,
    borderRadius: 12,
    background:
      theme.palette.type === "dark"
        ? "rgba(255,255,255,.03)"
        : "#F8FAFC",
    border: `1px solid ${
      theme.palette.type === "dark"
        ? "rgba(255,255,255,.055)"
        : "#E8EDF5"
    }`
  },

  detailLabel: {
    color: "#94A3B8",
    fontSize: "0.66rem",
    fontWeight: 750,
    textTransform: "uppercase",
    letterSpacing: ".04em"
  },

  detailValue: {
    marginTop: 4,
    color:
      theme.palette.type === "dark"
        ? "#F8FAFC"
        : "#0F172A",
    fontSize: "0.8rem",
    fontWeight: 800,
    wordBreak: "break-word"
  },

  clickableRow: {
    cursor: "pointer",
    transition: "background .15s ease",
    "&:hover": {
      background:
        theme.palette.type === "dark"
          ? "rgba(255,255,255,.025)"
          : "#F8FAFC"
    }
  },

  actionSection: {
    marginBottom: 20,
    padding: 14,
    borderRadius: 14,
    background:
      theme.palette.type === "dark"
        ? "rgba(255,255,255,.03)"
        : "#F8FAFC",
    border: `1px solid ${
      theme.palette.type === "dark"
        ? "rgba(255,255,255,.055)"
        : "#E8EDF5"
    }`
  },

  actionGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 10,
    marginTop: 12
  },

  activateButton: {
    minHeight: 40,
    borderRadius: 10,
    border: "1px solid rgba(34,197,94,.25)",
    background: "rgba(34,197,94,.10)",
    color: "#15803D",
    cursor: "pointer",
    fontFamily: "inherit",
    fontSize: "0.75rem",
    fontWeight: 850,
    "&:hover": {
      background: "rgba(34,197,94,.16)"
    },
    "&:disabled": {
      opacity: 0.45,
      cursor: "not-allowed"
    }
  },

  blockButton: {
    minHeight: 40,
    borderRadius: 10,
    border: "1px solid rgba(239,68,68,.24)",
    background: "rgba(239,68,68,.08)",
    color: "#B91C1C",
    cursor: "pointer",
    fontFamily: "inherit",
    fontSize: "0.75rem",
    fontWeight: 850,
    "&:hover": {
      background: "rgba(239,68,68,.14)"
    },
    "&:disabled": {
      opacity: 0.45,
      cursor: "not-allowed"
    }
  },

  companyToolbar: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    flexWrap: "wrap",
    marginBottom: 16
  },

  companySearch: {
    flex: "1 1 260px",
    minHeight: 40,
    padding: "9px 13px",
    borderRadius: 10,
    border: `1px solid ${
      theme.palette.type === "dark"
        ? "rgba(255,255,255,.09)"
        : "#E2E8F0"
    }`,
    background:
      theme.palette.type === "dark"
        ? "rgba(255,255,255,.035)"
        : "#FFFFFF",
    color:
      theme.palette.type === "dark"
        ? "#F8FAFC"
        : "#0F172A",
    fontFamily: "inherit",
    fontSize: "0.78rem",
    outline: "none",
    "&:focus": {
      borderColor: "#2563EB",
      boxShadow: "0 0 0 3px rgba(37,99,235,.08)"
    }
  },

  companyFilter: {
    minHeight: 40,
    padding: "9px 12px",
    borderRadius: 10,
    border: `1px solid ${
      theme.palette.type === "dark"
        ? "rgba(255,255,255,.09)"
        : "#E2E8F0"
    }`,
    background:
      theme.palette.type === "dark"
        ? "#111B2C"
        : "#FFFFFF",
    color:
      theme.palette.type === "dark"
        ? "#F8FAFC"
        : "#334155",
    fontFamily: "inherit",
    fontSize: "0.76rem",
    fontWeight: 700,
    outline: "none",
    cursor: "pointer"
  },

  companyMainName: {
    color:
      theme.palette.type === "dark"
        ? "#F8FAFC"
        : "#0F172A",
    fontSize: "0.78rem",
    fontWeight: 850
  },

  companySecondary: {
    marginTop: 3,
    color: "#94A3B8",
    fontSize: "0.65rem"
  },

  connectionBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
    padding: "4px 8px",
    borderRadius: 999,
    fontSize: "0.64rem",
    fontWeight: 800,
    whiteSpace: "nowrap"
  }

}));

const formatCurrency = value =>
  Number(value || 0).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL"
  });

const formatNumber = value =>
  Number(value || 0).toLocaleString("pt-BR");

const formatDate = value => {
  if (!value) return "—";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return String(value).slice(0, 10);
  }

  return date.toLocaleDateString("pt-BR", {
    timeZone: "America/Sao_Paulo"
  });
};

const formatDateTime = value => {
  if (!value) return "—";

  return new Date(value).toLocaleString("pt-BR", {
    dateStyle: "short",
    timeStyle: "short",
    timeZone: "America/Sao_Paulo"
  });
};

const recurrenceLabel = recurrence => {
  const normalized = String(
    recurrence || ""
  ).toUpperCase();

  if (normalized === "QUARTERLY") return "Trimestral";
  if (normalized === "YEARLY") return "Anual";
  return "Mensal";
};

const statusConfig = (status, companyStatus) => {
  const normalized = String(
    status || ""
  ).toLowerCase();

  if (
    companyStatus === false ||
    normalized === "expired"
  ) {
    return {
      label: "Vencida",
      color: "#B91C1C",
      background: "rgba(239,68,68,.11)"
    };
  }

  if (normalized === "active") {
    return {
      label: "Ativa",
      color: "#15803D",
      background: "rgba(34,197,94,.11)"
    };
  }

  if (normalized === "pending") {
    return {
      label: "Pendente",
      color: "#B45309",
      background: "rgba(245,158,11,.13)"
    };
  }

  if (normalized === "cancelled") {
    return {
      label: "Cancelada",
      color: "#475569",
      background: "rgba(100,116,139,.12)"
    };
  }

  return {
    label: status || "Não informado",
    color: "#475569",
    background: "rgba(100,116,139,.12)"
  };
};

const eventPresentation = event => {
  const labels = {
    webhook_received: "Webhook recebido",
    subscription_activated: "Assinatura ativada",
    credits_granted: "Créditos liberados",
    subscription_created: "Assinatura criada",
    subscription_cancelled: "Assinatura cancelada",
    subscription_renewed: "Assinatura renovada",
    plan_changed: "Plano alterado"
  };

  return labels[event] || event || "Evento da assinatura";
};

const DashboardMaster = () => {
  const classes = useStyles();
  const history = useHistory();
  const { user } = useContext(AuthContext);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const [detailOpen, setDetailOpen] =
    useState(false);

  const [detailLoading, setDetailLoading] =
    useState(false);

  const [selectedDetail, setSelectedDetail] =
    useState(null);

  const [companySearch, setCompanySearch] =
    useState("");

  const [companyFilter, setCompanyFilter] =
    useState("all");

  const [actionLoading, setActionLoading] =
    useState(false);

  const [activeModule, setActiveModule] =
    useState("overview");

  const handleModuleChange = moduleId => {
    const externalRoutes = {
      financial: "/financeiro",
      acquisition: "/dashboard-master/acquisition",
      customers: "/companies",
      ai: "/ai-credits-admin",
      infrastructure: "/server-metrics",
      settings: "/global-config",
      announcements: "/announcements"
    };

    if (externalRoutes[moduleId]) {
      history.push(
        externalRoutes[moduleId]
      );

      return;
    }

    setActiveModule(moduleId);
  };

  const isSuper =
    user?.super === true ||
    user?.profile === "super";

  const loadDashboard = useCallback(async () => {
    if (!isSuper) {
      setLoading(false);
      return;
    }

    setLoading(true);

    try {
      const response = await api.get(
        "/dashboard-master/overview"
      );

      setData(response.data);
    } catch (error) {
      toastError(error);
    } finally {
      setLoading(false);
    }
  }, [isSuper]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const openSubscriptionDetail = async subscriptionId => {
    setDetailOpen(true);
    setDetailLoading(true);
    setSelectedDetail(null);

    try {
      const response = await api.get(
        `/dashboard-master/subscriptions/${subscriptionId}`
      );

      setSelectedDetail(response.data);
    } catch (error) {
      toastError(error);
      setDetailOpen(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const executeSubscriptionAction = async action => {
    const subscriptionId =
      selectedDetail?.subscription?.id;

    if (!subscriptionId) {
      return;
    }

    const isActivate =
      action === "activate";

    const confirmed = window.confirm(
      isActivate
        ? "Deseja liberar esta assinatura manualmente?"
        : "Deseja bloquear esta assinatura manualmente?"
    );

    if (!confirmed) {
      return;
    }

    setActionLoading(true);

    try {
      await api.post(
        `/dashboard-master/subscriptions/${subscriptionId}/${action}`
      );

      toast.success(
        isActivate
          ? "Assinatura liberada com sucesso."
          : "Assinatura bloqueada com sucesso."
      );

      await Promise.all([
        loadDashboard(),
        openSubscriptionDetail(subscriptionId)
      ]);
    } catch (error) {
      toastError(error);
    } finally {
      setActionLoading(false);
    }
  };

  const kpis = data?.kpis || {};
  const credits = data?.credits || {};
  const companies = data?.companies || [];
  const subscriptions = data?.subscriptions || [];
  const renewals = data?.renewals || [];
  const recentEvents = data?.recentEvents || [];

  const financialChartData = [
    {
      name: "MRR",
      value: Number(kpis.mrr || 0)
    },
    {
      name: "Recebida",
      value: Number(kpis.receivedRevenue || 0)
    },
    {
      name: "ARR ÷ 12",
      value: Number(kpis.arr || 0) / 12
    }
  ];

  const subscriptionChartData = [
    {
      name: "Ativas",
      value: Number(kpis.activeCompanies || 0),
      color: "#22C55E"
    },
    {
      name: "Pendentes",
      value: Number(kpis.pendingSubscriptions || 0),
      color: "#F59E0B"
    },
    {
      name: "Vencidas",
      value: Number(kpis.expiredSubscriptions || 0),
      color: "#EF4444"
    },
    {
      name: "Canceladas",
      value: Number(kpis.cancelledSubscriptions || 0),
      color: "#64748B"
    }
  ];

  const connectedWhatsapps = Number(
    kpis.connectedWhatsapps || 0
  );

  const totalWhatsapps = Number(
    kpis.totalWhatsapps || 0
  );

  const disconnectedWhatsapps = Math.max(
    totalWhatsapps - connectedWhatsapps,
    0
  );

  const whatsappChartData = [
    {
      name: "Conectados",
      value: connectedWhatsapps,
      color: "#22C55E"
    },
    {
      name: "Desconectados",
      value: disconnectedWhatsapps,
      color: "#F59E0B"
    }
  ];

  const normalizedCompanySearch =
    companySearch
      .trim()
      .toLowerCase();

  const filteredCompanies =
    companies.filter(company => {
      const matchesSearch =
        !normalizedCompanySearch ||
        String(company.name || "")
          .toLowerCase()
          .includes(normalizedCompanySearch) ||
        String(company.email || "")
          .toLowerCase()
          .includes(normalizedCompanySearch) ||
        String(company.id || "")
          .includes(normalizedCompanySearch);

      if (!matchesSearch) {
        return false;
      }

      if (companyFilter === "with_subscription") {
        return Boolean(company.subscriptionId);
      }

      if (companyFilter === "without_subscription") {
        return !company.subscriptionId;
      }

      if (companyFilter === "connected") {
        return (
          Number(
            company.connectedWhatsappsCount || 0
          ) > 0
        );
      }

      if (companyFilter === "disconnected") {
        return (
          Number(company.whatsappsCount || 0) > 0 &&
          Number(
            company.connectedWhatsappsCount || 0
          ) === 0
        );
      }

      if (companyFilter === "blocked") {
        return company.status === false;
      }

      return true;
    });

  const kpiCards = useMemo(
    () => [
      {
        label: "Total de empresas",
        value: formatNumber(
          kpis.totalCompanies
        ),
        hint: "Empresas cadastradas na Korax",
        icon: "E",
        color: "#2563EB",
        background: "rgba(37,99,235,.10)"
      },
      {
        label: "Total de usuários",
        value: formatNumber(
          kpis.totalUsers
        ),
        hint: "Usuários cadastrados nas empresas",
        icon: "U",
        color: "#7C3AED",
        background: "rgba(124,58,237,.10)"
      },
      {
        label: "WhatsApps",
        value: `${formatNumber(
          kpis.connectedWhatsapps
        )}/${formatNumber(
          kpis.totalWhatsapps
        )}`,
        hint: "Conectados sobre cadastrados",
        icon: "W",
        color: "#15803D",
        background: "rgba(34,197,94,.11)"
      },
      {
        label: "Sem assinatura",
        value: formatNumber(
          kpis.companiesWithoutSubscription
        ),
        hint: "Empresas ainda fora do novo módulo",
        icon: "!",
        color: "#B45309",
        background: "rgba(245,158,11,.13)"
      },
      {
        label: "MRR",
        value: formatCurrency(kpis.mrr),
        hint: "Receita recorrente mensal estimada",
        icon: "R$",
        color: "#1D4ED8",
        background: "rgba(37,99,235,.10)"
      },
      {
        label: "ARR",
        value: formatCurrency(kpis.arr),
        hint: "Receita recorrente anual estimada",
        icon: "↗",
        color: "#7C3AED",
        background: "rgba(124,58,237,.10)"
      },
      {
        label: "Receita recebida",
        value: formatCurrency(
          kpis.receivedRevenue
        ),
        hint: "Pagamentos confirmados registrados",
        icon: "✓",
        color: "#15803D",
        background: "rgba(34,197,94,.11)"
      },
      {
        label: "Clientes ativos",
        value: formatNumber(
          kpis.activeCompanies
        ),
        hint: "Empresas com assinatura ativa",
        icon: "A",
        color: "#15803D",
        background: "rgba(34,197,94,.11)"
      },
      {
        label: "Pendentes",
        value: formatNumber(
          kpis.pendingSubscriptions
        ),
        hint: "Aguardando confirmação de pagamento",
        icon: "…",
        color: "#B45309",
        background: "rgba(245,158,11,.13)"
      },
      {
        label: "Vencidas",
        value: formatNumber(
          kpis.expiredSubscriptions
        ),
        hint: "Empresas bloqueadas ou vencidas",
        icon: "!",
        color: "#B91C1C",
        background: "rgba(239,68,68,.11)"
      },
      {
        label: "Renovam em 7 dias",
        value: formatNumber(
          kpis.renewalsNext7Days
        ),
        hint: "Renovações próximas",
        icon: "↻",
        color: "#0369A1",
        background: "rgba(14,165,233,.11)"
      },
      {
        label: "Canceladas",
        value: formatNumber(
          kpis.cancelledSubscriptions
        ),
        hint: "Assinaturas encerradas",
        icon: "×",
        color: "#475569",
        background: "rgba(100,116,139,.12)"
      }
    ],
    [kpis]
  );

  if (!isSuper) {
    return (
      <KoraxPage className={classes.root}>
        <div className={classes.denied}>
          <div
            style={{
              fontSize: 30,
              marginBottom: 10
            }}
          >
            🔒
          </div>

          <div
            style={{
              fontSize: "1.1rem",
              fontWeight: 850,
              color: "#0F172A"
            }}
          >
            Acesso restrito
          </div>

          <div
            style={{
              marginTop: 8,
              color: "#64748B",
              fontSize: "0.8rem",
              lineHeight: 1.6
            }}
          >
            Este painel é exclusivo para o usuário
            Master da Korax.
          </div>
        </div>
      </KoraxPage>
    );
  }

  return (
    <KoraxPage className={classes.root}>
      <div className={classes.inner}>
        <div className={classes.commandHero}>
          <div className={classes.heroTop}>
            <div>
              <div className={classes.heroEyebrow}>
                <span className={classes.liveDot} />
                Operação em tempo real
              </div>

              <h1 className={classes.heroTitle}>
                Centro de Comando Korax
              </h1>

              <div className={classes.heroSubtitle}>
                Acompanhe receita, clientes, assinaturas,
                conexões e créditos em uma única visão
                executiva da plataforma.
              </div>
            </div>

            <div className={classes.heroActions}>
              <button
                type="button"
                className={classes.heroRefresh}
                onClick={loadDashboard}
                disabled={loading}
              >
                {loading
                  ? "Atualizando..."
                  : "↻ Atualizar dados"}
              </button>
            </div>
          </div>

          <div className={classes.heroStats}>
            <div className={classes.heroStat}>
              <div className={classes.heroStatLabel}>
                Receita recorrente
              </div>

              <div className={classes.heroStatValue}>
                {formatCurrency(kpis.mrr)}
              </div>
            </div>

            <div className={classes.heroStat}>
              <div className={classes.heroStatLabel}>
                Clientes ativos
              </div>

              <div className={classes.heroStatValue}>
                {formatNumber(kpis.activeCompanies)}
              </div>
            </div>

            <div className={classes.heroStat}>
              <div className={classes.heroStatLabel}>
                WhatsApps conectados
              </div>

              <div className={classes.heroStatValue}>
                {formatNumber(kpis.connectedWhatsapps)}
                {" / "}
                {formatNumber(kpis.totalWhatsapps)}
              </div>
            </div>

            <div className={classes.heroStat}>
              <div className={classes.heroStatLabel}>
                Saldo de créditos
              </div>

              <div className={classes.heroStatValue}>
                {formatNumber(credits.currentBalance)}
              </div>
            </div>
          </div>
        </div>

        <ControlCenterNav
          activeModule={activeModule}
          onChange={handleModuleChange}
        />

        {activeModule === "releases" && (
          <ReleasesModule />
        )}

        {activeModule === "overview" && (
          <>
        <div className={classes.operationBar}>
          <div className={classes.operationItems}>
            <div className={classes.operationChip}>
              <span
                className={classes.chipDot}
                style={{ background: "#22C55E" }}
              />
              Plataforma operacional
            </div>

            <div className={classes.operationChip}>
              <span
                className={classes.chipDot}
                style={{
                  background:
                    Number(kpis.expiredSubscriptions || 0) > 0
                      ? "#EF4444"
                      : "#22C55E"
                }}
              />
              {formatNumber(
                kpis.expiredSubscriptions
              )} vencidas
            </div>

            <div className={classes.operationChip}>
              <span
                className={classes.chipDot}
                style={{
                  background:
                    Number(
                      kpis.pendingSubscriptions || 0
                    ) > 0
                      ? "#F59E0B"
                      : "#22C55E"
                }}
              />
              {formatNumber(
                kpis.pendingSubscriptions
              )} pagamentos pendentes
            </div>

            <div className={classes.operationChip}>
              <span
                className={classes.chipDot}
                style={{
                  background:
                    Number(
                      kpis.connectedWhatsapps || 0
                    ) <
                    Number(kpis.totalWhatsapps || 0)
                      ? "#F59E0B"
                      : "#22C55E"
                }}
              />
              Conexões monitoradas
            </div>
          </div>

          <div
            style={{
              color: "#94A3B8",
              fontSize: "0.66rem",
              fontWeight: 700
            }}
          >
            {data?.generatedAt
              ? `Sincronizado em ${formatDateTime(
                  data.generatedAt
                )}`
              : "Aguardando sincronização"}
          </div>
        </div>

        <div className={classes.sectionHeading}>
          <div>
            <div className={classes.sectionHeadingTitle}>
              Indicadores da operação
            </div>

            <div className={classes.sectionHeadingText}>
              Visão consolidada da estrutura comercial,
              financeira e operacional da Korax.
            </div>
          </div>
        </div>

        <div className={classes.kpiGrid}>
          {kpiCards.map(card => (
            <div
              key={card.label}
              className={classes.kpiCard}
            >
              <div className={classes.kpiTop}>
                <div className={classes.kpiLabel}>
                  {card.label}
                </div>

                <div
                  className={classes.kpiIcon}
                  style={{
                    color: card.color,
                    background: card.background
                  }}
                >
                  {card.icon}
                </div>
              </div>

              <div className={classes.kpiValue}>
                {card.value}
              </div>

              <div className={classes.kpiHint}>
                {card.hint}
              </div>
            </div>
          ))}
        </div>

        <div className={classes.chartsGrid}>
          <div className={classes.chartCard}>
            <div className={classes.chartHeader}>
              <div>
                <div className={classes.chartTitle}>
                  Panorama financeiro
                </div>

                <div className={classes.chartSubtitle}>
                  Comparativo entre receita recorrente
                  e pagamentos confirmados.
                </div>
              </div>
            </div>

            <div className={classes.chartBody}>
              <ResponsiveContainer
                width="100%"
                height="100%"
              >
                <BarChart
                  data={financialChartData}
                  margin={{
                    top: 10,
                    right: 8,
                    left: 0,
                    bottom: 0
                  }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    vertical={false}
                    stroke="rgba(148,163,184,.18)"
                  />

                  <XAxis
                    dataKey="name"
                    axisLine={false}
                    tickLine={false}
                    tick={{
                      fill: "#94A3B8",
                      fontSize: 11,
                      fontWeight: 700
                    }}
                  />

                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    width={72}
                    tick={{
                      fill: "#94A3B8",
                      fontSize: 10
                    }}
                    tickFormatter={value =>
                      Number(value || 0).toLocaleString(
                        "pt-BR",
                        {
                          notation: "compact",
                          maximumFractionDigits: 1
                        }
                      )
                    }
                  />

                  <Tooltip
                    cursor={{
                      fill: "rgba(37,99,235,.05)"
                    }}
                    formatter={value => [
                      formatCurrency(value),
                      "Valor"
                    ]}
                    contentStyle={{
                      borderRadius: 12,
                      border:
                        "1px solid rgba(148,163,184,.22)",
                      boxShadow:
                        "0 12px 30px rgba(15,23,42,.12)",
                      fontFamily:
                        "'Inter', system-ui, sans-serif",
                      fontSize: 12
                    }}
                  />

                  <Bar
                    dataKey="value"
                    radius={[8, 8, 3, 3]}
                    maxBarSize={62}
                  >
                    {financialChartData.map(
                      (entry, index) => (
                        <Cell
                          key={entry.name}
                          fill={
                            [
                              "#2563EB",
                              "#22C55E",
                              "#7C3AED"
                            ][index]
                          }
                        />
                      )
                    )}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className={classes.chartCard}>
            <div className={classes.chartHeader}>
              <div>
                <div className={classes.chartTitle}>
                  Saúde das assinaturas
                </div>

                <div className={classes.chartSubtitle}>
                  Distribuição atual da base de clientes.
                </div>
              </div>
            </div>

            <div className={classes.chartBody}>
              <ResponsiveContainer
                width="100%"
                height="100%"
              >
                <PieChart>
                  <Pie
                    data={subscriptionChartData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="48%"
                    innerRadius={58}
                    outerRadius={88}
                    paddingAngle={3}
                    stroke="none"
                  >
                    {subscriptionChartData.map(
                      entry => (
                        <Cell
                          key={entry.name}
                          fill={entry.color}
                        />
                      )
                    )}
                  </Pie>

                  <Tooltip
                    formatter={(value, name) => [
                      formatNumber(value),
                      name
                    ]}
                    contentStyle={{
                      borderRadius: 12,
                      border:
                        "1px solid rgba(148,163,184,.22)",
                      boxShadow:
                        "0 12px 30px rgba(15,23,42,.12)",
                      fontFamily:
                        "'Inter', system-ui, sans-serif",
                      fontSize: 12
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>

            <div className={classes.chartLegend}>
              {subscriptionChartData.map(item => (
                <div
                  key={item.name}
                  className={classes.chartLegendItem}
                >
                  <span
                    className={classes.chartLegendDot}
                    style={{
                      background: item.color
                    }}
                  />

                  {item.name}:{" "}
                  {formatNumber(item.value)}
                </div>
              ))}
            </div>
          </div>

          <div className={classes.chartCard}>
            <div className={classes.chartHeader}>
              <div>
                <div className={classes.chartTitle}>
                  Conexões WhatsApp
                </div>

                <div className={classes.chartSubtitle}>
                  Visão consolidada das conexões cadastradas.
                </div>
              </div>
            </div>

            <div className={classes.chartBody}>
              <ResponsiveContainer
                width="100%"
                height="100%"
              >
                <PieChart>
                  <Pie
                    data={whatsappChartData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="48%"
                    innerRadius={58}
                    outerRadius={88}
                    paddingAngle={3}
                    stroke="none"
                  >
                    {whatsappChartData.map(
                      entry => (
                        <Cell
                          key={entry.name}
                          fill={entry.color}
                        />
                      )
                    )}
                  </Pie>

                  <Tooltip
                    formatter={(value, name) => [
                      formatNumber(value),
                      name
                    ]}
                    contentStyle={{
                      borderRadius: 12,
                      border:
                        "1px solid rgba(148,163,184,.22)",
                      boxShadow:
                        "0 12px 30px rgba(15,23,42,.12)",
                      fontFamily:
                        "'Inter', system-ui, sans-serif",
                      fontSize: 12
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>

            <div className={classes.chartLegend}>
              {whatsappChartData.map(item => (
                <div
                  key={item.name}
                  className={classes.chartLegendItem}
                >
                  <span
                    className={classes.chartLegendDot}
                    style={{
                      background: item.color
                    }}
                  />

                  {item.name}:{" "}
                  {formatNumber(item.value)}
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className={classes.card}>
          <div className={classes.cardHeader}>
            <div>
              <div className={classes.cardTitle}>
                Créditos da plataforma
              </div>

              <div className={classes.cardSubtitle}>
                Visão consolidada de créditos liberados,
                vendidos e consumidos.
              </div>
            </div>
          </div>

          <div className={classes.creditGrid}>
            <div className={classes.creditCard}>
              <div
                className={classes.creditValue}
                style={{ color: "#16A34A" }}
              >
                +
                {formatNumber(credits.granted)}
              </div>
              <div className={classes.creditLabel}>
                Créditos liberados
              </div>
            </div>

            <div className={classes.creditCard}>
              <div
                className={classes.creditValue}
                style={{ color: "#DC2626" }}
              >
                −
                {formatNumber(credits.consumed)}
              </div>
              <div className={classes.creditLabel}>
                Créditos consumidos
              </div>
            </div>

            <div className={classes.creditCard}>
              <div
                className={classes.creditValue}
                style={{ color: "#1D4ED8" }}
              >
                {formatNumber(
                  credits.currentBalance
                )}
              </div>
              <div className={classes.creditLabel}>
                Saldo total das empresas
              </div>
            </div>

            <div className={classes.creditCard}>
              <div
                className={classes.creditValue}
                style={{ color: "#7C3AED" }}
              >
                {formatNumber(
                  credits.subscriptionCredits
                )}
              </div>
              <div className={classes.creditLabel}>
                Créditos por assinatura
              </div>
            </div>

            <div className={classes.creditCard}>
              <div
                className={classes.creditValue}
                style={{ color: "#D97706" }}
              >
                {formatNumber(
                  credits.packageCredits
                )}
              </div>
              <div className={classes.creditLabel}>
                Créditos em pacotes
              </div>
            </div>
          </div>
        </div>

        <div className={classes.card}>
          <div className={classes.cardHeader}>
            <div>
              <div className={classes.cardTitle}>
                Todas as empresas
              </div>

              <div className={classes.cardSubtitle}>
                Controle central de usuários, conexões, créditos, planos e assinaturas.
              </div>
            </div>

            <div
              style={{
                color: "#64748B",
                fontSize: "0.72rem",
                fontWeight: 800
              }}
            >
              {formatNumber(
                filteredCompanies.length
              )} de{" "}
              {formatNumber(companies.length)}
            </div>
          </div>

          <div className={classes.companyToolbar}>
            <input
              type="text"
              className={classes.companySearch}
              value={companySearch}
              onChange={event =>
                setCompanySearch(
                  event.target.value
                )
              }
              placeholder="Buscar por empresa, e-mail ou ID..."
            />

            <select
              className={classes.companyFilter}
              value={companyFilter}
              onChange={event =>
                setCompanyFilter(
                  event.target.value
                )
              }
            >
              <option value="all">
                Todas
              </option>

              <option value="with_subscription">
                Com assinatura
              </option>

              <option value="without_subscription">
                Sem assinatura
              </option>

              <option value="connected">
                WhatsApp conectado
              </option>

              <option value="disconnected">
                WhatsApp desconectado
              </option>

              <option value="blocked">
                Empresas bloqueadas
              </option>
            </select>
          </div>

          {filteredCompanies.length === 0 ? (
            <div className={classes.empty}>
              Nenhuma empresa encontrada.
            </div>
          ) : (
            <div className={classes.tableWrap}>
              <table
                className={classes.table}
                style={{ minWidth: 1180 }}
              >
                <thead>
                  <tr>
                    <th className={classes.th}>
                      Empresa
                    </th>

                    <th className={classes.th}>
                      Usuários
                    </th>

                    <th className={classes.th}>
                      WhatsApps
                    </th>

                    <th className={classes.th}>
                      Créditos
                    </th>

                    <th className={classes.th}>
                      Consumo
                    </th>

                    <th className={classes.th}>
                      Plano
                    </th>

                    <th className={classes.th}>
                      Assinatura
                    </th>

                    <th className={classes.th}>
                      Vencimento
                    </th>

                    <th className={classes.th}>
                      Empresa
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {filteredCompanies.map(company => {
                    const hasSubscription =
                      Boolean(
                        company.subscriptionId
                      );

                    const connected =
                      Number(
                        company.connectedWhatsappsCount ||
                        0
                      );

                    const totalWhatsapps =
                      Number(
                        company.whatsappsCount || 0
                      );

                    const subscriptionLabel =
                      company.subscriptionSituation ===
                      "active"
                        ? "Ativa"
                        : company.subscriptionSituation ===
                          "pending"
                          ? "Pendente"
                          : company.subscriptionSituation ===
                            "blocked"
                            ? "Bloqueada"
                            : company.subscriptionSituation ===
                              "cancelled"
                              ? "Cancelada"
                              : "Sem assinatura";

                    const subscriptionColor =
                      company.subscriptionSituation ===
                      "active"
                        ? "#15803D"
                        : company.subscriptionSituation ===
                          "pending"
                          ? "#B45309"
                          : company.subscriptionSituation ===
                            "blocked"
                            ? "#B91C1C"
                            : "#64748B";

                    const subscriptionBackground =
                      company.subscriptionSituation ===
                      "active"
                        ? "rgba(34,197,94,.11)"
                        : company.subscriptionSituation ===
                          "pending"
                          ? "rgba(245,158,11,.13)"
                          : company.subscriptionSituation ===
                            "blocked"
                            ? "rgba(239,68,68,.11)"
                            : "rgba(100,116,139,.11)";

                    return (
                      <tr
                        key={company.id}
                        className={
                          classes.clickableRow
                        }
                        onClick={() =>
                          history.push(
                            `/dashboard-master/companies/${company.id}`
                          )
                        }
                      >
                        <td className={classes.td}>
                          <div
                            className={
                              classes.companyMainName
                            }
                          >
                            {company.name ||
                              `Empresa #${company.id}`}
                          </div>

                          <div
                            className={
                              classes.companySecondary
                            }
                          >
                            ID {company.id}
                            {company.email
                              ? ` • ${company.email}`
                              : ""}
                          </div>
                        </td>

                        <td className={classes.td}>
                          <strong>
                            {formatNumber(
                              company.usersCount
                            )}
                          </strong>

                          <div
                            className={
                              classes.companySecondary
                            }
                          >
                            {formatNumber(
                              company.adminsCount
                            )}{" "}
                            administradores
                          </div>
                        </td>

                        <td className={classes.td}>
                          <span
                            className={
                              classes.connectionBadge
                            }
                            style={{
                              color:
                                connected > 0
                                  ? "#15803D"
                                  : totalWhatsapps > 0
                                    ? "#B45309"
                                    : "#64748B",
                              background:
                                connected > 0
                                  ? "rgba(34,197,94,.11)"
                                  : totalWhatsapps > 0
                                    ? "rgba(245,158,11,.13)"
                                    : "rgba(100,116,139,.11)"
                            }}
                          >
                            {connected}/
                            {totalWhatsapps} conectados
                          </span>
                        </td>

                        <td className={classes.td}>
                          <strong
                            style={{
                              color: "#1D4ED8"
                            }}
                          >
                            {formatNumber(
                              company.creditBalance
                            )}
                          </strong>
                        </td>

                        <td className={classes.td}>
                          {formatNumber(
                            company.creditsConsumed
                          )}
                        </td>

                        <td className={classes.td}>
                          <strong>
                            {company.koraxPlanName ||
                              company.legacyPlanName ||
                              "Sem plano"}
                          </strong>
                        </td>

                        <td className={classes.td}>
                          <span
                            className={
                              classes.statusBadge
                            }
                            style={{
                              color:
                                subscriptionColor,
                              background:
                                subscriptionBackground
                            }}
                          >
                            {subscriptionLabel}
                          </span>
                        </td>

                        <td className={classes.td}>
                          {formatDate(
                            company.nextDueDate ||
                            company.expiresAt ||
                            company.dueDate
                          )}
                        </td>

                        <td className={classes.td}>
                          <span
                            className={
                              classes.statusBadge
                            }
                            style={{
                              color:
                                company.status === false
                                  ? "#B91C1C"
                                  : "#15803D",
                              background:
                                company.status === false
                                  ? "rgba(239,68,68,.11)"
                                  : "rgba(34,197,94,.11)"
                            }}
                          >
                            {company.status === false
                              ? "Bloqueada"
                              : "Ativa"}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className={classes.sectionGrid}>
          <div className={classes.card}>
            <div className={classes.cardHeader}>
              <div>
                <div className={classes.cardTitle}>
                  Assinaturas
                </div>

                <div className={classes.cardSubtitle}>
                  Última assinatura de cada empresa.
                </div>
              </div>

              <div
                style={{
                  color: "#64748B",
                  fontSize: "0.72rem",
                  fontWeight: 750
                }}
              >
                {formatNumber(subscriptions.length)} registros
              </div>
            </div>

            {loading && !data ? (
              <div className={classes.empty}>
                Carregando assinaturas...
              </div>
            ) : subscriptions.length === 0 ? (
              <div className={classes.empty}>
                Nenhuma assinatura encontrada.
              </div>
            ) : (
              <div className={classes.tableWrap}>
                <table className={classes.table}>
                  <thead>
                    <tr>
                      <th className={classes.th}>
                        Empresa
                      </th>
                      <th className={classes.th}>
                        Plano
                      </th>
                      <th className={classes.th}>
                        Status
                      </th>
                      <th className={classes.th}>
                        Valor
                      </th>
                      <th className={classes.th}>
                        Pagamento
                      </th>
                      <th className={classes.th}>
                        Vencimento
                      </th>
                      <th className={classes.th}>
                        Créditos
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {subscriptions.map(subscription => {
                      const status = statusConfig(
                        subscription.status,
                        subscription.companyStatus
                      );

                      const dueDate =
                        subscription.nextDueDate ||
                        subscription.expiresAt ||
                        subscription.companyDueDate;

                      return (
                        <tr
                          key={subscription.id}
                          className={classes.clickableRow}
                          onClick={() =>
                            openSubscriptionDetail(
                              subscription.id
                            )
                          }
                        >
                          <td className={classes.td}>
                            <div
                              style={{
                                fontWeight: 800,
                                color: "#0F172A"
                              }}
                            >
                              {subscription.companyName ||
                                `Empresa #${subscription.companyId}`}
                            </div>

                            <div
                              style={{
                                marginTop: 3,
                                color: "#94A3B8",
                                fontSize: "0.66rem"
                              }}
                            >
                              ID {subscription.companyId}
                            </div>
                          </td>

                          <td className={classes.td}>
                            <div
                              style={{
                                fontWeight: 750
                              }}
                            >
                              {subscription.planName ||
                                "Plano não informado"}
                            </div>

                            <div
                              style={{
                                marginTop: 3,
                                color: "#94A3B8",
                                fontSize: "0.65rem"
                              }}
                            >
                              {recurrenceLabel(
                                subscription.recurrence
                              )}
                            </div>
                          </td>

                          <td className={classes.td}>
                            <span
                              className={
                                classes.statusBadge
                              }
                              style={{
                                color: status.color,
                                background:
                                  status.background
                              }}
                            >
                              {status.label}
                            </span>
                          </td>

                          <td className={classes.td}>
                            <strong>
                              {formatCurrency(
                                subscription.planPrice
                              )}
                            </strong>
                          </td>

                          <td className={classes.td}>
                            {subscription.paymentMethod ||
                              "—"}
                          </td>

                          <td className={classes.td}>
                            {formatDate(dueDate)}
                          </td>

                          <td className={classes.td}>
                            {formatNumber(
                              subscription.creditsIncluded
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className={classes.card}>
            <div className={classes.cardHeader}>
              <div>
                <div className={classes.cardTitle}>
                  Eventos recentes
                </div>

                <div className={classes.cardSubtitle}>
                  Timeline de pagamentos e assinaturas.
                </div>
              </div>
            </div>

            {recentEvents.length === 0 ? (
              <div className={classes.empty}>
                Nenhum evento registrado ainda.
              </div>
            ) : (
              <div className={classes.timeline}>
                {recentEvents
                  .slice(0, 12)
                  .map(event => (
                    <div
                      key={event.id}
                      className={classes.timelineItem}
                    >
                      <div
                        className={classes.timelineDot}
                      >
                        ✓
                      </div>

                      <div>
                        <div
                          className={classes.eventTitle}
                        >
                          {eventPresentation(
                            event.event
                          )}
                        </div>

                        <div
                          className={classes.eventMeta}
                        >
                          {event.companyName ||
                            `Empresa #${event.companyId}`}
                          {event.planName
                            ? ` • ${event.planName}`
                            : ""}
                          {" • "}
                          {formatDateTime(
                            event.createdAt
                          )}
                        </div>

                        {event.description && (
                          <div
                            className={classes.eventMeta}
                          >
                            {event.description}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
              </div>
            )}
          </div>
        </div>

        <div className={classes.card}>
          <div className={classes.cardHeader}>
            <div>
              <div className={classes.cardTitle}>
                Próximas renovações
              </div>

              <div className={classes.cardSubtitle}>
                Assinaturas que vencem nos próximos 30 dias.
              </div>
            </div>
          </div>

          {renewals.length === 0 ? (
            <div className={classes.empty}>
              Nenhuma renovação prevista para os próximos
              30 dias.
            </div>
          ) : (
            <div className={classes.tableWrap}>
              <table className={classes.table}>
                <thead>
                  <tr>
                    <th className={classes.th}>
                      Empresa
                    </th>
                    <th className={classes.th}>
                      Plano
                    </th>
                    <th className={classes.th}>
                      Recorrência
                    </th>
                    <th className={classes.th}>
                      Valor
                    </th>
                    <th className={classes.th}>
                      Renovação
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {renewals.map(renewal => (
                    <tr key={renewal.id}>
                      <td className={classes.td}>
                        <strong>
                          {renewal.companyName ||
                            `Empresa #${renewal.companyId}`}
                        </strong>
                      </td>

                      <td className={classes.td}>
                        {renewal.planName || "—"}
                      </td>

                      <td className={classes.td}>
                        {recurrenceLabel(
                          renewal.recurrence
                        )}
                      </td>

                      <td className={classes.td}>
                        {formatCurrency(
                          renewal.planPrice
                        )}
                      </td>

                      <td className={classes.td}>
                        {formatDate(
                          renewal.renewalDate
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {data?.generatedAt && (
          <div
            style={{
              color: "#94A3B8",
              fontSize: "0.65rem",
              textAlign: "right"
            }}
          >
            Atualizado em{" "}
            {formatDateTime(data.generatedAt)}
          </div>
        )}
          </>

        )}
      </div>

      <Drawer
        anchor="right"
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        classes={{
          paper: classes.drawerPaper
        }}
      >
        <div className={classes.drawerHeader}>
          <div>
            <div className={classes.cardTitle}>
              Detalhes da assinatura
            </div>

            <div className={classes.cardSubtitle}>
              Visão completa da empresa, cobrança e eventos.
            </div>
          </div>

          <button
            type="button"
            className={classes.refreshButton}
            onClick={() => setDetailOpen(false)}
          >
            Fechar
          </button>
        </div>

        <div className={classes.drawerBody}>
          {detailLoading ? (
            <div className={classes.empty}>
              Carregando detalhes...
            </div>
          ) : !selectedDetail ? (
            <div className={classes.empty}>
              Nenhum detalhe disponível.
            </div>
          ) : (
            <>
              <div className={classes.actionSection}>
                <div className={classes.cardTitle}>
                  Ações administrativas
                </div>

                <div className={classes.cardSubtitle}>
                  Libere ou bloqueie o acesso desta empresa. A ação será registrada na timeline.
                </div>

                <div className={classes.actionGrid}>
                  <button
                    type="button"
                    className={classes.activateButton}
                    disabled={
                      actionLoading ||
                      (
                        selectedDetail.subscription?.status === "active" &&
                        selectedDetail.company?.status === true
                      )
                    }
                    onClick={() =>
                      executeSubscriptionAction(
                        "activate"
                      )
                    }
                  >
                    {actionLoading
                      ? "Processando..."
                      : "✓ Liberar empresa"}
                  </button>

                  <button
                    type="button"
                    className={classes.blockButton}
                    disabled={
                      actionLoading ||
                      selectedDetail.company?.status === false ||
                      selectedDetail.subscription?.status === "blocked"
                    }
                    onClick={() =>
                      executeSubscriptionAction(
                        "block"
                      )
                    }
                  >
                    {actionLoading
                      ? "Processando..."
                      : "Bloquear empresa"}
                  </button>
                </div>
              </div>

              <div className={classes.detailGrid}>
                <div className={classes.detailItem}>
                  <div className={classes.detailLabel}>
                    Empresa
                  </div>
                  <div className={classes.detailValue}>
                    {selectedDetail.company?.name || "—"}
                  </div>
                </div>

                <div className={classes.detailItem}>
                  <div className={classes.detailLabel}>
                    Status
                  </div>
                  <div className={classes.detailValue}>
                    {selectedDetail.subscription?.status || "—"}
                  </div>
                </div>

                <div className={classes.detailItem}>
                  <div className={classes.detailLabel}>
                    Plano
                  </div>
                  <div className={classes.detailValue}>
                    {selectedDetail.plan?.name || "—"}
                  </div>
                </div>

                <div className={classes.detailItem}>
                  <div className={classes.detailLabel}>
                    Valor
                  </div>
                  <div className={classes.detailValue}>
                    {formatCurrency(
                      selectedDetail.plan?.price
                    )}
                  </div>
                </div>

                <div className={classes.detailItem}>
                  <div className={classes.detailLabel}>
                    Vencimento
                  </div>
                  <div className={classes.detailValue}>
                    {formatDate(
                      selectedDetail.subscription?.nextDueDate ||
                      selectedDetail.subscription?.expiresAt ||
                      selectedDetail.company?.dueDate
                    )}
                  </div>
                </div>

                <div className={classes.detailItem}>
                  <div className={classes.detailLabel}>
                    Pagamento
                  </div>
                  <div className={classes.detailValue}>
                    {selectedDetail.company?.paymentMethod || "—"}
                  </div>
                </div>

                <div className={classes.detailItem}>
                  <div className={classes.detailLabel}>
                    Payment ID
                  </div>
                  <div className={classes.detailValue}>
                    {selectedDetail.subscription?.providerSubscriptionId || "—"}
                  </div>
                </div>

                <div className={classes.detailItem}>
                  <div className={classes.detailLabel}>
                    Cliente Asaas
                  </div>
                  <div className={classes.detailValue}>
                    {selectedDetail.subscription?.asaasCustomerId || "—"}
                  </div>
                </div>

                <div className={classes.detailItem}>
                  <div className={classes.detailLabel}>
                    Saldo de créditos
                  </div>
                  <div className={classes.detailValue}>
                    {formatNumber(
                      selectedDetail.credits?.balance
                    )}
                  </div>
                </div>

                <div className={classes.detailItem}>
                  <div className={classes.detailLabel}>
                    Créditos do plano
                  </div>
                  <div className={classes.detailValue}>
                    {formatNumber(
                      selectedDetail.subscription?.creditsIncluded
                    )}
                  </div>
                </div>
              </div>

              <div className={classes.cardTitle}>
                Responsáveis
              </div>

              <div
                style={{
                  marginTop: 10,
                  marginBottom: 18
                }}
              >
                {(selectedDetail.responsibleUsers || []).map(userItem => (
                  <div
                    key={userItem.id}
                    className={classes.detailItem}
                    style={{ marginBottom: 8 }}
                  >
                    <div className={classes.detailValue}>
                      {userItem.name}
                    </div>
                    <div className={classes.eventMeta}>
                      {userItem.email} • {userItem.profile}
                    </div>
                  </div>
                ))}
              </div>

              <div className={classes.cardTitle}>
                Eventos
              </div>

              <div
                className={classes.timeline}
                style={{ marginTop: 12 }}
              >
                {(selectedDetail.events || []).length === 0 ? (
                  <div className={classes.empty}>
                    Nenhum evento registrado.
                  </div>
                ) : (
                  selectedDetail.events.map(event => (
                    <div
                      key={event.id}
                      className={classes.timelineItem}
                    >
                      <div className={classes.timelineDot}>
                        ✓
                      </div>

                      <div>
                        <div className={classes.eventTitle}>
                          {eventPresentation(event.event)}
                        </div>

                        <div className={classes.eventMeta}>
                          {formatDateTime(event.createdAt)}
                          {event.billingType
                            ? ` • ${event.billingType}`
                            : ""}
                        </div>

                        {event.description && (
                          <div className={classes.eventMeta}>
                            {event.description}
                          </div>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>

              <div
                className={classes.cardTitle}
                style={{ marginTop: 22 }}
              >
                Webhooks
              </div>

              <div style={{ marginTop: 10 }}>
                {(selectedDetail.webhooks || []).length === 0 ? (
                  <div className={classes.empty}>
                    Nenhum webhook registrado.
                  </div>
                ) : (
                  selectedDetail.webhooks.map(webhook => (
                    <div
                      key={webhook.id}
                      className={classes.detailItem}
                      style={{ marginBottom: 8 }}
                    >
                      <div className={classes.detailValue}>
                        {webhook.event}
                      </div>

                      <div className={classes.eventMeta}>
                        {webhook.processed
                          ? "Processado"
                          : "Não processado"}
                        {" • "}
                        {formatDateTime(webhook.createdAt)}
                      </div>

                      {webhook.error && (
                        <div
                          className={classes.eventMeta}
                          style={{ color: "#DC2626" }}
                        >
                          {webhook.error}
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>

              {selectedDetail.subscription?.lastInvoiceUrl && (
                <a
                  href={selectedDetail.subscription.lastInvoiceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={classes.refreshButton}
                  style={{
                    display: "block",
                    textAlign: "center",
                    textDecoration: "none",
                    marginTop: 18
                  }}
                >
                  Abrir cobrança no Asaas
                </a>
              )}
            </>
          )}
        </div>
      </Drawer>
    </KoraxPage>
  );
};

export default DashboardMaster;
